// PATH: src/hooks/useRecentStudents.ts — derniers élèves consultés.
//
// Le travail d'un secrétariat tourne autour de quelques dossiers à la fois : les
// rouvrir ne devrait pas repasser par une recherche. La liste est locale au
// poste (localStorage) — c'est une commodité de navigation, pas une donnée
// d'établissement.
import { useCallback, useState } from "react";

const STORAGE_KEY = "tilmischool.recentStudents";
const MAX = 5;

export interface RecentStudent {
  id: number;
  name: string;
}

const read = (): RecentStudent[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // Le contenu vient du poste : on ne lui fait pas confiance aveuglément.
    return Array.isArray(parsed)
      ? parsed.filter((r): r is RecentStudent => typeof r?.id === "number" && typeof r?.name === "string")
      : [];
  } catch {
    return [];
  }
};

/** pushRecentStudent — hors composant : appelable depuis n'importe quel écran. */
export const pushRecentStudent = (student: RecentStudent): void => {
  try {
    // Dédoublonné puis tronqué : le dossier rouvert remonte en tête plutôt que
    // d'occuper deux places.
    const next = [student, ...read().filter((r) => r.id !== student.id)].slice(0, MAX);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible : la commodité disparaît, rien de plus */
  }
};

/**
 * useRecentStudents — liste au moment du montage, plus un `refresh` explicite.
 * Pas d'abonnement au stockage : les écrans qui l'affichent (palette de
 * recherche, tableau de bord) se montent après la consultation qu'ils listent.
 */
export const useRecentStudents = (): { recent: RecentStudent[]; refresh: () => void } => {
  const [recent, setRecent] = useState<RecentStudent[]>(read);
  const refresh = useCallback(() => setRecent(read()), []);
  return { recent, refresh };
};
